import React, { useState, useEffect, useRef } from "react";
import { navLinks, companyInfo } from "../data/mockData";
import { equipmentCategoryList } from "../data/equipmentData";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const Navbar = ({ onBookingOpen }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    setDropdownOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
    } else if (!isHome && href.startsWith('#')) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const showDark = scrolled || !isHome;

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          showDark
            ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          {/* Logos */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { navigate('/'); window.scrollTo(0,0); }}
              className="flex items-center group"
            >
              <img
                src="/uploads/o6xalfl3_logo1.png"
                alt="Brinckmann Arbeitsbühnen"
                className={`h-10 object-contain transition-all duration-300 ${
                  showDark ? "brightness-0" : "brightness-100"
                }`}
              />
            </button>
            <span className={`text-xs select-none ${showDark ? "text-[#d1d1d6]" : "text-white/30"}`}>|</span>
            <a
              href="https://www.partnerlift.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
            >
              <img
                src="/uploads/i5n868it_t5dfo6kp_Logo_PartnerLIFT-Einfarbig-ohne.png"
                alt="PartnerLIFT"
                className={`h-8 object-contain transition-all duration-300 ${
                  showDark ? "brightness-0" : "brightness-0 invert"
                }`}
              />
            </a>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => {
              if (link.label === 'Geräte') {
                return (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`flex items-center gap-1 text-xs transition-colors duration-300 hover:opacity-70 ${
                        showDark ? "text-[#1d1d1f]" : "text-white/90"
                      }`}
                    >
                      Geräte
                      <ChevronDown size={12} className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {dropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-black/10 border border-[#e5e5e5] overflow-hidden py-2"
                        >
                          {equipmentCategoryList.map((cat) => (
                            <button
                              key={cat.slug}
                              onClick={() => {
                                setDropdownOpen(false);
                                navigate(`/geraete/${cat.slug}`);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-[#1d1d1f] hover:bg-[#f5f5f7] hover:text-[#4a7c59] transition-colors"
                            >
                              {cat.name}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className={`text-xs transition-colors duration-300 hover:opacity-70 ${
                    showDark ? "text-[#1d1d1f]" : "text-white/90"
                  }`}
                >
                  {link.label}
                </button>
              );
            })}
            <a
              href={`tel:+49${companyInfo.phone.replace(/\D/g, '').replace(/^0/, '')}`}
              className="flex items-center gap-2 bg-[#4a7c59] hover:bg-[#3d6a4a] text-white text-xs px-4 py-2 rounded-full transition-all duration-300 hover:scale-105"
            >
              <Phone size={12} />
              {companyInfo.phone}
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden transition-colors ${
              showDark ? "text-[#1d1d1f]" : "text-white"
            }`}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white/95 backdrop-blur-xl pt-20 px-8 overflow-y-auto pb-8"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => {
                if (link.label === 'Geräte') {
                  return (
                    <div key={link.label}>
                      <p className="text-2xl font-semibold text-[#1d1d1f] mb-3">Geräte</p>
                      <div className="flex flex-col gap-2 pl-4">
                        {equipmentCategoryList.map((cat) => (
                          <button
                            key={cat.slug}
                            onClick={() => {
                              setMobileOpen(false);
                              navigate(`/geraete/${cat.slug}`);
                            }}
                            className="text-lg text-[#6e6e73] text-left hover:text-[#4a7c59] transition-colors"
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    className="text-2xl font-semibold text-[#1d1d1f] text-left hover:text-[#4a7c59] transition-colors"
                  >
                    {link.label}
                  </button>
                );
              })}
              <a
                href={`tel:+49${companyInfo.phone.replace(/\D/g, '').replace(/^0/, '')}`}
                onClick={() => setMobileOpen(false)}
                className="mt-4 flex items-center justify-center gap-2 bg-[#4a7c59] text-white text-base px-6 py-3 rounded-full"
              >
                <Phone size={16} />
                {companyInfo.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
